import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    question: 'Quels services propose VISINOT ?',
    answer:
      'VISINOT propose deux packs principaux : le Pack Vision (photos HD, plans 2D, pose/dépose panneau) et le Pack Visites Immobilières (organisation et réalisation des visites pendant 2 mois, rapports détaillés). Des services complémentaires sont également disponibles : vide-maison, nettoyage et ouverture forcée.',
  },
  {
    question: 'Comment fonctionne le Pack Vision ?',
    answer:
      'Vous nous communiquez les informations du bien et nous organisons une séance photo professionnelle. Vous recevez des photos HD, des plans 2D et nous gérons la pose et dépose du panneau de vente. Le tout livré sous 48h.',
  },
  {
    question: 'Comment fonctionne le Pack Visites Immobilières ?',
    answer:
      'Vous nous transmettez les informations du bien à vendre. Nous organisons et réalisons les visites pendant une durée de 2 mois, 6 jours sur 7. Après chaque session, vous recevez un rapport détaillé avec le nombre de visiteurs et leurs observations.',
  },
  {
    question: 'Quels types de biens pouvez-vous couvrir ?',
    answer:
      'Nous intervenons sur tous types de biens immobiliers : maisons, appartements, terrains, immeubles de rapport, locaux commerciaux. Que ce soit pour des ventes Biddit, des ventes publiques ou des ventes de gré à gré.',
  },
  {
    question: 'Dans quelle zone géographique intervenez-vous ?',
    answer:
      'Nous intervenons principalement en Belgique, en Wallonie et à Bruxelles. Pour des demandes spécifiques en dehors de ces zones, n\'hésitez pas à nous contacter pour vérifier la disponibilité.',
  },
  {
    question: 'Quelles sont les options disponibles pour les visites ?',
    answer:
      'En plus du pack de base, vous pouvez ajouter l\'option visites le dimanche, des créneaux supplémentaires, ou encore des visites en soirée. Tout est modulable selon vos besoins.',
  },
  {
    question: 'Comment fonctionnent vos services complémentaires ?',
    answer:
      'Le vide-maison est proposé sur devis personnalisé en fonction du volume et de la nature des objets. Le nettoyage est également sur devis. L\'ouverture forcée par serrurier professionnel est un service à tarif fixe.',
  },
  {
    question: 'Pouvez-vous intervenir rapidement ?',
    answer:
      'Oui, nous comprenons les contraintes de temps liées aux ventes notariales. Nous nous engageons à répondre sous 24h et pouvons organiser une première intervention très rapidement après validation de votre commande.',
  },
];

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="bg-white rounded-xl px-6 shadow-sm mb-3">
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between gap-4 text-left hover:text-gold-600 transition-colors group"
      >
        <span className="text-lg font-display font-semibold text-navy-600 group-hover:text-gold-600 transition-colors">
          {question}
        </span>
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold-50 flex items-center justify-center group-hover:bg-gold-100 transition-colors">
          {isOpen ? (
            <Minus className="w-5 h-5 text-gold-600" />
          ) : (
            <Plus className="w-5 h-5 text-gold-600" />
          )}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pb-5 text-navy-300 leading-relaxed pr-12">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-24 bg-cream/30">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 text-gold-700 text-sm font-medium border border-gold-200 mb-6"
          >
            FAQ
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-display font-bold text-navy-600 mb-6"
          >
            Questions{' '}
            <span className="gradient-text">fréquentes</span>
          </motion.h2>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <FAQItem
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
